import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/lib/api";
import { Receipt, Euro, Calendar, Store } from "lucide-react";

/**
 * Receipt thumbnail + OCR badges used in the participants table.
 *  - No receipt → em-dash.
 *  - Receipt → 36px square preview, clicking opens the full-size photo + OCR
 *    (amount, date, merchant) in a dialog so the merchant can verify at the
 *    till.
 *
 * The API stores `/api/files/...` paths. `API` is already the `/api` prefix,
 * so we strip it to avoid doubling the prefix.
 */
function resolveUrl(receiptUrl) {
  if (!receiptUrl) return null;
  const base = API.replace(/\/api$/, "");
  if (/^https?:\/\//.test(receiptUrl)) return receiptUrl;
  return `${base}${receiptUrl.startsWith("/") ? receiptUrl : `/${receiptUrl}`}`;
}

export default function ReceiptCell({ receiptUrl, ocr }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const full = resolveUrl(receiptUrl);

  if (!full) {
    return (
      <span
        className="text-xs text-muted-foreground"
        data-testid="receipt-cell-empty"
      >
        —
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-2 py-1 transition-colors hover:bg-muted"
        data-testid="receipt-cell-thumb"
      >
        <span className="relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-black/40">
          
          <img
            src={full}
            alt="Ticket"
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </span>
        {ocr?.total_amount != null && (
          <span className="text-[11px] font-semibold tabular-nums text-foreground">
            {Number(ocr.total_amount).toFixed(2)}
            <span className="ml-0.5 text-[10px] text-muted-foreground">
              {ocr.currency || "EUR"}
            </span>
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              {t("dashboard.receipt_dialog_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            {ocr && (
              <div className="flex flex-wrap gap-2 text-xs">
                {ocr.total_amount != null && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-1">
                    <Euro className="h-3 w-3 text-amber-500" />
                    <span className="font-semibold">
                      {Number(ocr.total_amount).toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">
                      {ocr.currency || "EUR"}
                    </span>
                  </span>
                )}
                {ocr.date && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-1">
                    <Calendar className="h-3 w-3 text-amber-500" />
                    {ocr.date}
                  </span>
                )}
                {ocr.merchant_name && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-1">
                    <Store className="h-3 w-3 text-amber-500" />
                    <span className="max-w-[16ch] truncate">
                      {ocr.merchant_name}
                    </span>
                  </span>
                )}
                {typeof ocr.confidence === "number" && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-1">
                    {t("dashboard.receipt_confidence", {
                      value: ocr.confidence,
                    })}
                  </span>
                )}
              </div>
            )}
            <div className="overflow-hidden rounded-xl border border-border bg-black/10">
              
              <img
                src={full}
                alt="Ticket"
                className="max-h-[70vh] w-full object-contain"
              />
            </div>
            <a
              href={full}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-xs text-muted-foreground hover:text-foreground"
            >
              {t("dashboard.receipt_open_full")}
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
